<template>
  <v-list v-if="profile">
    <!-- ID No & Birth Date -->
    <v-list-tile
      @click="copiedToast(profile.identification)"
      v-if="profile.identification"
      v-clipboard:copy="profile.identification"
    >
      <v-list-tile-avatar>
        <v-tooltip bottom>
          <span
            slot="activator"
            v-if="profile.countryOfBirth"
            :class="
              `flag-icon flag-icon-${profile.countryOfBirth.toLowerCase()}`
            "
          />
          <span>Nationality: {{ countryName(profile.countryOfBirth) }}</span>
        </v-tooltip>
      </v-list-tile-avatar>
      <v-list-tile-content>
        <v-list-tile-title class="text-xs-left">
          <span class="ml-3">{{ profile.identification }}</span>
        </v-list-tile-title>
        <v-list-tile-sub-title
          class="ml-3 caption text-xs-left"
          v-if="missing('IdentityDocument')"
        >
          <v-icon small left :class="themes.client.light.warningText">
            mdi-alert-circle
          </v-icon>
          <span :class="themes.client.light.warningText">
            Identity Document Required
          </span>
        </v-list-tile-sub-title>
        <v-list-tile-sub-title
          class="ml-3 caption text-xs-left"
          v-else-if="profile.countryOfBirth == 'ZA'"
        >
          <span v-if="profile.citizen"> Citizen </span>
          <span v-if="!profile.citizen"> Foreign National </span>
        </v-list-tile-sub-title>
      </v-list-tile-content>
    </v-list-tile>

    <!-- Birth Date -->
    <v-list-tile @click="" v-if="profile.bornAt">
      <v-list-tile-avatar>
        <v-tooltip bottom>
          <v-icon slot="activator">mdi-cake-variant</v-icon>
          <span>Date of Birth</span>
        </v-tooltip>
      </v-list-tile-avatar>
      <v-list-tile-content>
        <v-tooltip bottom>
          <v-list-tile-content slot="activator">
            <span class="ml-3">{{
              new Date(profile.bornAt) | date("d MMMM yyy")
            }}</span>
          </v-list-tile-content>
          <span>
            Birthday on {{ new Date(profile.bornAt) | date("d MMMM yyy") }} ({{
              new Date(profile.bornAt) | inWords({ addSuffix: true })
            }})
          </span>
        </v-tooltip>
      </v-list-tile-content>
    </v-list-tile>

    <!-- Gender -->
    <v-list-tile @click="" v-if="profile.gender">
      <v-list-tile-avatar>
        <v-tooltip bottom>
          <v-icon slot="activator">mdi-human-male-female</v-icon>
          <span>Gender Identification</span>
        </v-tooltip>
      </v-list-tile-avatar>
      <v-tooltip bottom>
        <v-list-tile-content slot="activator">
          <span class="ml-3">
            {{ profile.gender }}
          </span>
        </v-list-tile-content>
        <span class=""> Identifies as {{ profile.gender }} </span>
      </v-tooltip>
    </v-list-tile>

    <!-- Ethnicity -->
    <v-list-tile @click="" v-if="profile.ethnicity">
      <v-list-tile-avatar>
        <v-tooltip bottom>
          <v-icon slot="activator">mdi-earth</v-icon>
          <span>Ethnicity</span>
        </v-tooltip>
      </v-list-tile-avatar>
      <v-tooltip bottom>
        <v-list-tile-content slot="activator">
          <span class="ml-3">
            {{ profile.ethnicity }}
          </span>
        </v-list-tile-content>
        <span class=""> Ethnicity is {{ profile.ethnicity }} </span>
      </v-tooltip>
    </v-list-tile>

    <!-- Disability -->
    <v-list-tile @click="" v-if="profile.disabled">
      <v-list-tile-avatar>
        <v-tooltip bottom>
          <v-icon slot="activator">mdi-wheelchair-accessibility</v-icon>
          <span>Limiting disability</span>
        </v-tooltip>
      </v-list-tile-avatar>
      <v-list-tile-content>
        <span class="ml-3" v-if="profile.disabilityCategory">
          {{ categories[profile.disabilityCategory] }}
        </span>
        <span class="ml-3" v-else>Yes</span>
      </v-list-tile-content>
    </v-list-tile>

    <!-- Language Skills -->
    <v-list-tile @click="" v-if="showLanguages">
      <v-list-tile-avatar>
        <v-tooltip bottom>
          <v-icon slot="activator">mdi-account-voice</v-icon>
          <span>Language Skills</span>
        </v-tooltip>
      </v-list-tile-avatar>
      <v-list-tile-content class="ml-3">
        <v-layout align-center justify-space-between row fill-height>
          <v-flex
            v-for="(language, i) in profile.languages"
            :key="`language-${i}`"
            v-if="i < 5"
          >
            <span v-if="i > 0">, </span>
            <v-tooltip bottom>
              <span slot="activator">{{ language.language }}</span>
              <span> {{ language.description }} </span>
            </v-tooltip>
          </v-flex>
        </v-layout>
      </v-list-tile-content>
    </v-list-tile>

    <!-- Work Rights -->
    <v-list-tile @click="" v-if="showWorkRights">
      <v-list-tile-avatar>
        <v-tooltip bottom>
          <v-icon slot="activator">mdi-briefcase</v-icon>
          <span>Work Rights</span>
        </v-tooltip>
      </v-list-tile-avatar>
      <v-list-tile-content class="ml-3">
        <v-layout align-center justify-space-between row fill-height>
          <v-flex
            v-for="(right, i) in profile.workRights"
            :key="`work-right-${i}`"
          >
            <v-tooltip bottom>
              <span
                slot="activator"
                v-if="right.countryCode"
                :class="
                  `flag-icon flag-icon-${right.countryCode.toLowerCase()}`
                "
              />
              <span> {{ right.country }} </span>
            </v-tooltip>
          </v-flex>
        </v-layout>
      </v-list-tile-content>
    </v-list-tile>

    <!-- Driving License -->
    <v-list-tile @click="" v-if="profile.driversLicense">
      <v-list-tile-avatar>
        <v-icon>mdi-card-account-details</v-icon>
      </v-list-tile-avatar>
      <v-list-tile-content>
        <span class="ml-3"> Valid driver's license </span>
      </v-list-tile-content>
    </v-list-tile>

    <!-- Current Location -->
    <v-list-tile @click="" v-if="profile.location">
      <v-list-tile-avatar>
        <v-tooltip bottom>
          <v-icon slot="activator">mdi-map-marker</v-icon>
          <span>Current Location</span>
        </v-tooltip>
      </v-list-tile-avatar>
      <v-list-tile-content>
        <span class="ml-3">
          {{ profile.location.address }}
        </span>
      </v-list-tile-content>
    </v-list-tile>
  </v-list>
</template>

<script>
// Data
import categories from "@/data/disability_categories";
import countries from "@/data/countries";

export default {
  props: { profile: Object, actionItems: Array },
  data() {
    return {
      countries,
      categories: categories.reduce((acc, i) => {
        acc[i.value] = i.text;
        return acc;
      }, {})
    };
  },
  computed: {
    showWorkRights() {
      return this.profile.workRights && this.profile.workRights[0];
    },
    showLanguages() {
      return this.profile.languages && this.profile.languages[0];
    },
    showStudyRights() {
      return this.profile.studyRights && this.profile.studyRights[0];
    },
    showPersonalDetails() {
      return true;
      return (
        this.profile.countryOfBirth ||
        this.profile.bornAt ||
        this.profile.identification ||
        this.profile.gender
      );
    },
    columns() {
      let count = 0;
      if (this.showWorkRights || this.showStudyRights) count++;
      if (this.showPersonalDetails) count++;
      if (this.showInterests || this.showCompetencies || this.showAffiliations)
        count++;
      return count;
    }
  },
  methods: {
    countryName(code) {
      const country = this.countries.find(country => country.code === code);
      return country ? country.name : "";
    },
    missing(item) {
      if (!this.actionItems) return [];
      return this.actionItems.some(a => a.type.includes(item));
    },
    copiedToast(value) {
      this.$store.dispatch("snackbarShow", `Copied ${value}.`);
    }
  }
};
</script>
